import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Tabs,
  Tab,
  Box,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
} from "@mui/material";
import api from "../../../API/axios";
import NotificationSnackbar from "../../../components/NotificationSnackbar";
import useNotification from "../../../hooks/useNotification";
import { useAuthApi } from "../../../hooks/useAuthApi";
import { countries } from "../../../utils/country";
import { handleFieldChange } from "../../../utils/handleFieldChange";
import { sanitizeInput } from "../../../utils/inputSanitizer";

function TabPanel({ children, value, index }) {
  return (
    <div style={{ display: value === index ? "block" : "none", width: "100%" }}>
      <Box sx={{ p: 2 }}>{children}</Box>
    </div>
  );
}

export default function EditUserTabs() {
  const { sendResetPasswordRequest } = useAuthApi();

  // Removed isRegistered state, backend handles update restrictions
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [clients, setClients] = useState([]);
  const [isRegistered, setIsRegistered] = useState(false);
  const { notification, showNotification, hideNotification } =
    useNotification();

  // Details tab state
  const [details, setDetails] = useState({
    name: "",
    email: "",
    telephone: "",
    address: "",
    address2: "",
    country: "GB",
    state: "",
    city: "",
    postCode: "",
    enabled: true,
    twoFactorMethod: "",
    role: "",
    clientName: "",
  });

  const [phoneCode, setPhoneCode] = useState(
    countries.find((c) => c.code === details.country)?.phoneCode || "+44"
  );

  useEffect(() => {
    const selected = countries.find((c) => c.code === details.country);
    setPhoneCode(selected ? selected.phoneCode : "+44");
  }, [details.country]);

  // Access tab state
  const [selectedClients, setSelectedClients] = useState([]);

  // QR Code state
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [qrCodeLoading, setQrCodeLoading] = useState(false);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    async function fetchUserAndClients() {
      setLoading(true);
      try {
        // Fetch user details
        const userRes = await api.get(`/users/${id}`);
        const userData =
          userRes.data && userRes.data.data ? userRes.data.data : userRes.data;

        // --- Phone code extraction logic ---
        let telephone = userData.Telephone || userData.telephone || "";
        let countryCode = userData.Country || userData.country || "GB";
        let phoneCode =
          countries.find((c) => c.code === countryCode)?.phoneCode || "+44";
        // Remove phone code from telephone if present
        if (telephone.startsWith(phoneCode)) {
          telephone = telephone.replace(phoneCode, "").trim();
        }

        setUser(userData);
        setDetails({
          name: userData.name || "",
          email: userData.email || "",
          telephone: telephone,
          address: userData.Address || userData.address || "",
          address2: userData.Address2 || userData.address2 || "",
          country: userData.Country || userData.country || "GB",
          state: userData.CountryState || userData.countryState || "",
          city: userData.TownCity || userData.townCity || "",
          postCode: userData.PostCode || userData.postCode || "",
          enabled: userData.status === 1 || userData.status === true,
          twoFactorMethod: userData.twoFactorMethod || "",
          role: userData.role || "",
          clientName: userData.clientName || "",
        });
        // Registration check: must be verified and have twoFactorMethod set (not 'none')
        setIsRegistered(
          (userData.isVerified === true || userData.IsVerified === true) &&
            userData.twoFactorMethod &&
            userData.twoFactorMethod !== "none"
        );

        setSelectedClients(userData.clientIds || []);

        // Fetch all clients for access tab
        const clientsRes = await api.get("/clients");
        const clientsData =
          clientsRes.data && clientsRes.data.data
            ? clientsRes.data.data
            : clientsRes.data;
        setClients(Array.isArray(clientsData) ? clientsData : []);
      } catch (err) {
        console.error("Error fetching user or clients:", err);
        showNotification("Failed to load user details", "error");
      }
      setLoading(false);
    }

    if (id) {
      fetchUserAndClients();
    }
  }, [id]);

  const handleDetailsChange = (field, value) => {
    setDetails((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateDetails = () => {
    const newErrors = {};
    if (!details.name.trim()) newErrors.name = "Name is required";
    if (!details.email.trim()) newErrors.email = "Email is required";
    return newErrors;
  };

  const handleSubmitDetails = async () => {
    const newErrors = validateDetails();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      showNotification("Please fix the validation errors", "error");
      return;
    }

    // Prefix phone code
    const selectedCountry = countries.find((c) => c.code === details.country);
    const phoneCode = selectedCountry ? selectedCountry.phoneCode : "+44";
    const telephoneWithCode = details.telephone.startsWith(phoneCode)
      ? details.telephone
      : `${phoneCode}${details.telephone.trim()}`;

    try {
      const payload = {
        name: details.name,
        email: details.email,
        telephone: telephoneWithCode,
        address: details.address,
        address2: details.address2,
        country: details.country,
        countryState: details.state,
        townCity: details.city,
        postCode: details.postCode,
        status: details.enabled, // boolean
        twoFactorMethod: details.twoFactorMethod,
        role: details.role || "User", // Default to 'User' if not set
        clientIds: selectedClients, // Use the selected clients from Access tab
        clientName: details.clientName || "",
      };
      console.log("Submitting user details:", payload);
      await api.put(`/users/${id}`, payload);
      showNotification("User details updated successfully", "success");
    } catch (err) {
      console.error("Error updating user details:", err);
      showNotification("Failed to update user details", "error");
      if (
        err &&
        err.response &&
        err.response.data &&
        err.response.data.message
      ) {
        showNotification(err.response.data.message, "error");
      }
    }
  };

  if (isRegistered) {
    console.log("user isRegistered");
  }

  const handleSubmitAccess = async () => {
    // Validate at least one client is selected
    if (!selectedClients || selectedClients.length === 0) {
      showNotification("Please select at least one client", "error");
      return;
    }

    try {
      const payload = {
        clientIds: selectedClients,
      };

      await api.put(`/users/${id}/clients`, payload);
      showNotification("User access updated successfully", "success");
    } catch (err) {
      console.error("Error updating user access:", err);
      showNotification("Failed to update user access", "error");
    }
  };

  const generateUserQRCode = async () => {
    if (details.twoFactorMethod !== "app") return;

    setQrCodeLoading(true);
    try {
      const response = await api.post(`/users/${id}/generate-qr`);
      const qrUrl =
        response.data?.data?.qrCodeUrl || response.data?.qrCodeUrl || "";
      setQrCodeUrl(qrUrl);
    } catch (err) {
      console.error("Failed to generate QR code:", err);
      showNotification("Failed to generate QR code", "error");
    }
    setQrCodeLoading(false);
  };

  // Generate QR code when twoFactorMethod changes to 'app'
  useEffect(() => {
    if (details.twoFactorMethod === "app" && user) {
      generateUserQRCode();
    }
  }, [details.twoFactorMethod, user]);

  const handleResetPassword = async () => {
    const result = await sendResetPasswordRequest(id);
    if (result) {
      showNotification("Password reset email sent successfully!", "success");
    } else {
      showNotification("Failed to send password reset email.", "error");
    }
  };

  const handleClientChange = (event) => {
    const value = event.target.value;
    setSelectedClients(typeof value === "string" ? value.split(",") : value);
  };

  if (loading) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Typography>Loading user details...</Typography>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Typography color="error">User not found</Typography>
        <Button onClick={() => navigate("/superadmin/users")} sx={{ mt: 2 }}>
          Back to Users
        </Button>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <div>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ color: "var(--color-primary)", fontWeight: 700 }}
          >
            EDIT USER
          </Typography>
        </div>
        <Button
          variant="outlined"
          onClick={() => navigate("/superadmin/users")}
          sx={{
            color: "#fff",
            borderColor: "#635f5fff",
            backgroundColor: "#635f5fff",
            "&:hover": { backgroundColor: "#333", borderColor: "#333" },
          }}
        >
          Back to Users
        </Button>
      </div>

      {/* Tabs */}
      <Paper sx={{ width: "100%" }}>
        <Tabs
          value={tab}
          onChange={(e, newValue) => setTab(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              color: "#666",
              "&.Mui-selected": {
                color: "var(--color-primary)",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "var(--color-primary)",
            },
          }}
        >
          <Tab label="Details" />
          <Tab label="Access" />
        </Tabs>

        {/* Details Tab */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "var(--color-primary)", fontWeight: 600, mb: 2 }}
            >
              User Details
            </Typography>

            <Box sx={{ display: "flex", gap: 4 }}>
              {/* Left Column - Main Details */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  flex: 1,
                  maxWidth: 600,
                }}
              >
                {/* Basic Information */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                >
                  Basic Information
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Name"
                    name="name"
                    required
                    autoFocus
                    value={details.name}
                    onChange={(e) =>
                      handleFieldChange(
                        "name",
                        sanitizeInput(e.target.value),
                        setDetails,
                        setErrors
                      )
                    }
                    error={!!errors.name}
                    helperText={errors.name}
                    inputProps={{ maxLength: 30 }}
                    size="medium"
                    placeholder="Full Name"
                    sx={{ width: 270, minWidth: 180, background: "#fff" }}
                  />

                  <TextField
                    label="Email"
                    name="email"
                    value={details.email}
                    onChange={(e) =>
                      handleFieldChange(
                        "email",
                        e.target.value,
                        setDetails,
                        setErrors
                      )
                    }
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                    size="medium"
                    placeholder="Email Address"
                    inputProps={{ maxLength: 50 }}
                    sx={{ width: 270, minWidth: 180, background: "#fff" }}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Telephone"
                  name="telephone"
                  value={details.telephone}
                  required
                  onChange={(e) =>
                    handleDetailsChange(
                      "telephone",
                      e.target.value.replace(/[^0-9]/g, "")
                    )
                  }
                  sx={{ width: 550, borderRadius: 2, background: "#fff" }}
                  error={!!errors.telephone}
                  helperText={errors.telephone}
                  inputProps={{ maxLength: 15 }}
                  InputProps={{
                    startAdornment: (
                      <Box
                        sx={{
                          minWidth: 64,
                          mr: 1,
                          pr: 1,
                          borderRight: "2px solid #E9E9E9",
                        }}
                      >
                        {phoneCode}
                      </Box>
                    ),
                  }}
                />

                {/* Address Information */}
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#333", mb: 1, mt: 2 }}
                >
                  Address Information
                </Typography>

                {/* Address and Address2 in same row */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="Address"
                    name="address"
                    required
                    value={details.address}
                    // onChange={(e) =>  handleDetailsChange("address", e.target.value)}
                    onChange={(e) =>
                      handleFieldChange(
                        "address",
                        e.target.value,
                        setDetails,
                        setErrors
                      )
                    }
                    size="medium"
                    error={!!errors.address}
                    helperText={errors.address}
                    placeholder="Street Address"
                    inputProps={{ maxLength: 30 }}
                    sx={{
                      width: 270,
                      minWidth: 180,
                      borderRadius: 2,
                      background: "#fff",
                    }}
                  />

                  <TextField
                    label="Address 2"
                    name="address2"
                    value={details.address2}
                    onChange={(e) =>
                      handleDetailsChange("address2", e.target.value)
                    }
                    size="medium"
                    placeholder="Apartment, Suite, etc."
                    sx={{
                      width: 270,
                      minWidth: 180,
                      borderRadius: 2,
                      background: "#fff",
                    }}
                    inputProps={{ maxLength: 30 }}
                  />
                </Box>

                {/* State and City Row */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="County/State"
                    name="state"
                    required
                    value={details.state}
                    //onChange={(e) =>    handleDetailsChange("state", e.target.value)      }
                    onChange={(e) =>
                      handleFieldChange(
                        "state",
                        e.target.value,
                        setDetails,
                        setErrors
                      )
                    }
                    size="medium"
                    placeholder="County/State"
                    error={!!errors.state}
                    helperText={errors.state}
                    inputProps={{ maxLength: 30 }}
                    sx={{
                      width: 270,
                      minWidth: 180,
                      borderRadius: 2,
                      background: "#fff",
                    }}
                  />

                  <TextField
                    label="Town/City"
                    name="city"
                    required
                    value={details.city}
                    // onChange={(e) => handleDetailsChange("city", e.target.value) }
                    onChange={(e) =>
                      handleFieldChange(
                        "city",
                        e.target.value,
                        setDetails,
                        setErrors
                      )
                    }
                    size="medium"
                    placeholder="Town/City"
                    error={!!errors.city}
                    helperText={errors.city}
                    inputProps={{ maxLength: 30 }}
                    sx={{
                      width: 270,
                      minWidth: 180,
                      borderRadius: 2,
                      background: "#fff",
                    }}
                  />
                </Box>

                {/* PostCode and Country Row */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    label="PostCode/ZipCode"
                    name="postCode"
                    inputProps={{
                      maxLength: 10,
                    }}
                    value={details.postCode}
                    // onChange={(e) => handleDetailsChange("postCode", e.target.value) }
                    onChange={(e) => {
                      handleFieldChange(
                        "postCode",
                        e.target.value,
                        setDetails,
                        setErrors
                      );
                    }}
                    error={!!errors.postCode}
                    helperText={errors.postCode}
                    size="medium"
                    placeholder="PostCode/ZipCode"
                    sx={{
                      width: 270,
                      minWidth: 180,
                      borderRadius: 2,
                      background: "#fff",
                    }}
                  />

                  <Box sx={{ width: 270, minWidth: 180, background: "#fff" }}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      size="medium"
                      sx={{
                        borderRadius: 2,
                        background: "#fff",
                        width: "100%",
                        height: 40,
                      }}
                    >
                      <InputLabel
                        htmlFor="country-select"
                        sx={{
                          transform: "translate(14px, 18px) scale(0.9)",
                          "&.MuiInputLabel-shrink": {
                            transform: "translate(14px, 0px) scale(0.75)",
                          },
                        }}
                      >
                        Country
                      </InputLabel>
                      <Select
                        name="country"
                        required
                        value={details.country}
                        onChange={(e) =>
                          handleDetailsChange("country", e.target.value)
                        }
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 220,
                              overflowY: "auto",
                            },
                          },
                        }}
                        input={<OutlinedInput label="Country" />}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>

              {/* Right Column - Account Settings */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minWidth: 300,
                  maxWidth: 350,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                >
                  Account Settings
                </Typography>

                <Paper
                  sx={{
                    p: 2,
                    backgroundColor: "#fff",
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                  }}
                >
                  {/* Show toggle only if 2FA method is set */}
                  {details.twoFactorMethod &&
                    details.twoFactorMethod !== "none" && (
                      <>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={details.enabled}
                              onChange={(e) =>
                                handleDetailsChange("enabled", e.target.checked)
                              }
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: "#43a047", // light green
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                  {
                                    backgroundColor: "#43a047",
                                  },
                              }}
                            />
                          }
                          label="User Enabled"
                          sx={{ mb: 2 }}
                        />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mb: 3, fontSize: "0.875rem" }}
                        >
                          Disabling a user will log them out and prevent them
                          from logging in until re-enabled.
                        </Typography>
                      </>
                    )}

                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                  >
                    Two-Factor Authentication
                  </Typography>

                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Method:
                    </Typography>
                    <Chip
                      label={
                        !details.twoFactorMethod ||
                        details.twoFactorMethod === "none"
                          ? "Pending"
                          : details.twoFactorMethod === "email"
                          ? "Email"
                          : "Authenticator App"
                      }
                      color={
                        !details.twoFactorMethod ||
                        details.twoFactorMethod === "none"
                          ? "warning"
                          : details.twoFactorMethod === "email"
                          ? "error"
                          : "secondary"
                      }
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </Box>

                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mb: 3, display: "block", fontSize: "0.75rem" }}
                  >
                    The 2FA method is set during user registration and cannot be
                    changed here.
                  </Typography>

                  {/* QR Code Display for Authenticator App */}
                  {details.twoFactorMethod === "app" && (
                    <Box sx={{ mt: 2, mb: 3 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                      >
                        Authenticator App QR Code
                      </Typography>
                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "#f9f9f9",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                          textAlign: "center",
                        }}
                      >
                        {qrCodeLoading ? (
                          <Typography variant="body2" color="textSecondary">
                            Generating QR Code...
                          </Typography>
                        ) : qrCodeUrl ? (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{ mb: 1, fontWeight: 500 }}
                            >
                              Scan with authenticator app:
                            </Typography>
                            <img
                              src={qrCodeUrl}
                              alt="QR Code for 2FA setup"
                              style={{
                                maxWidth: "180px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "white",
                              }}
                            />
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ display: "block", mt: 1 }}
                            >
                              Use Google Authenticator, Microsoft Authenticator,
                              or similar apps
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            <Typography
                              variant="body2"
                              color="error"
                              sx={{ mb: 1 }}
                            >
                              Failed to generate QR code
                            </Typography>
                            <Button
                              size="small"
                              onClick={generateUserQRCode}
                              sx={{ fontSize: "0.75rem" }}
                            >
                              Retry
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Action Buttons moved below card */}
                  {/* Action Buttons below card */}
                </Paper>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    minWidth: 300,
                    maxWidth: 350,
                  }}
                >
                  <Button
                    disabled={
                      !details.twoFactorMethod ||
                      details.twoFactorMethod === "none" ||
                      Object.values(errors).some((msg) => msg)
                    }
                    onClick={handleSubmitDetails}
                    // disabled={
                    //   !details.twoFactorMethod ||
                    //   details.twoFactorMethod === "none"
                    // }
                    variant="contained"
                    sx={{
                      backgroundColor: "var(--color-primary)",
                      "&:hover": { backgroundColor: "var(--color-secondary)" },
                    }}
                  >
                    Update Details
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleResetPassword}
                    disabled={
                      !details.twoFactorMethod ||
                      details.twoFactorMethod === "none"
                    }
                    sx={{
                      color: "#fff",
                      borderColor: "#635f5fff",
                      backgroundColor: "#635f5fff",
                      "&:hover": {
                        backgroundColor: "#635f5fff",
                      },
                    }}
                  >
                    Reset Password
                  </Button>
                  {(!details.twoFactorMethod ||
                    details.twoFactorMethod === "none") && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5 }}
                    >
                      User must complete two-factor registration before
                      performing account actions.
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </TabPanel>

        {/* Access Tab */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "var(--color-primary)", fontWeight: 600, mb: 2 }}
            >
              Client Access
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Select which clients this user will have visibility over.
            </Typography>
            <FormControl
              fullWidth
              variant="outlined"
              sx={{ mt: 2, mb: 2, borderRadius: 2, background: "#fff" }}
            >
              <InputLabel id="assign-clients-label">
                Assign Clients
              </InputLabel>
              <Select
                labelId="assign-clients-label"
                multiple
                value={selectedClients}
                onChange={handleClientChange}
                input={<OutlinedInput label="Assign Clients" />}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      overflowY: "auto",
                    },
                  },
                }}
                renderValue={(selected) => {
                  // Show chips for up to 8 clients, then show count
                  if (selected.length <= 8) {
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((clientId) => {
                          const client = clients.find((c) => c.id === clientId);
                          return (
                            <Chip
                              key={clientId}
                              label={client ? client.name : `Client ${clientId}`}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    );
                  } else {
                    // Show first 5 chips + count of remaining
                    const displayedClients = selected.slice(0, 5);
                    const remainingCount = selected.length - 5;
                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                        {displayedClients.map((clientId) => {
                          const client = clients.find((c) => c.id === clientId);
                          return (
                            <Chip
                              key={clientId}
                              label={client ? client.name : `Client ${clientId}`}
                              size="small"
                            />
                          );
                        })}
                        <Chip
                          label={`+${remainingCount} more`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    );
                  }
                }}
              >
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    <Checkbox checked={selectedClients.includes(client.id)} />
                    <ListItemText primary={client.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              <Button
                variant="contained"
                onClick={handleSubmitAccess}
                sx={{
                  mt: 2,
                  backgroundColor: "var(--color-primary)",
                  "&:hover": { backgroundColor: "var(--color-secondary)" },
                }}
              >
                Update Access
              </Button>
            </Box>
          </Box>
        </TabPanel>
      </Paper>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </div>
  );
}
